import { format } from 'date-fns';

const MILLISECONDS_IN_A_MINUTE = 60000;

export const utcToLocal = (date: Date) => {
  if (!date) {
    return date;
  }
  return new Date(date.getTime() + date.getTimezoneOffset() * MILLISECONDS_IN_A_MINUTE);
};

export const localToUTC = (date: Date) => {
  if (!date) {
    return date;
  }
  return new Date(date.getTime() - date.getTimezoneOffset() * MILLISECONDS_IN_A_MINUTE);
};

/**
 * Converts UTC date strings to local Date objects for payout rules display
 * Handles ISO date strings that come from the backend
 */
export const payoutUtcToLocal = (date: string | Date) => {
  if (!date) {
    return date;
  }

  // Convert string to Date object if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Check if it's a valid date
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    console.warn('payoutUtcToLocal: Invalid date object, returning original:', date);
    return date;
  }

  return new Date(dateObj.getTime() + dateObj.getTimezoneOffset() * MILLISECONDS_IN_A_MINUTE);
};

export const midnight = (date: Date) => {
  if (!date) {
    return date;
  }
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const formatAnnualDate = (dateTimestamp) => {
  const date = new Date(dateTimestamp);
  const userTimezoneUtcOffsetMs = date.getTimezoneOffset() * 60 * 1000;
  // calculate for offset only when backend already save birthday not in UTC and ISO 8601 format
  const dateInput = date.getUTCHours() === 0 ? date.getTime() : date.getTime() - userTimezoneUtcOffsetMs;
  return new Date(dateInput).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatDateInYYYYMMDD = (isoDate: string): string => {
  const date = new Date(isoDate);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a Date or date string as a local datetime string for <input type="datetime-local">.
 * Example output: '2024-06-12T14:30'
 * @param date Date object or ISO string
 * @returns Local datetime string for input value
 */
export function toLocalDatetimeInputValue(date: Date | string | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Formats a date as MM/DD/YYYY (e.g., 04/27/2024).
 * Use for all table cell dates, exports, and anywhere a standard US date format is needed.
 * @param date - Date object or ISO string
 * @returns Formatted date string or empty string if invalid
 */
export function formatDateMMDDYYYY(date: string | Date): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return format(d, 'MM/dd/yyyy');
}
